// import {showMessage} from 'exsportia-components'
import {showMessage} from 'exsportia-components'
import {useEffect, useMemo, useState} from 'react'
import {useMutation, useQuery} from 'react-query'
import {useNavigate, useParams} from 'react-router-dom'

import {BookingQueries} from '../../../api/booking'
import {PaymentsMutations} from '../../../api/payments'
import {useLocale} from '../../../utils/i18n'
import {usePaymentProvider} from './payment-provider-form.provider'

type entity = string | null
type OpenPaymentFormArgsType = {
  totalPrice?: number
  bookingId?: string
  entity: entity
  startProcessCallback?: () => void
}
export type openPaymentFormType = (params: OpenPaymentFormArgsType) => void

type HandleDraftEntityStatusChangeType = {
  entity: entity
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  entityId: string | null
  posTerminalProcessId?: string | null
  redirectPath?: string | null
  // Lets the caller take over post-payment navigation (e.g. show a modal of all created
  // bookings for a multi-session booking). Return true to skip the default single redirect.
  onBookingPaymentSuccess?: () => boolean | void
  // External-link pay page: send a failed charge to the Payment Failed page (as card does)
  // instead of a toast. Ignored for POS sessions, which keep the toast.
  redirectOnFail?: boolean
}
export const useHandleDraftEntityStatusChange = ({
  entity,
  entityId,
  isLoading,
  setIsLoading,
  posTerminalProcessId,
  redirectPath: redirectPathOverride,
  onBookingPaymentSuccess,
  redirectOnFail,
}: HandleDraftEntityStatusChangeType) => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {clearUrls} = usePaymentProvider()
  const [startRefetch, setStartRefetch] = useState(false)
  const {venueId} = useParams<{venueId: string}>()
  const {mutate: updateEntityForPay} = useMutation(PaymentsMutations.UpdateEntityForPayMutation({}))
  const mustRefetch = useMemo(() => startRefetch, [startRefetch])
  const {
    data: draftEntity,
    error,
    remove,
    isLoading: draftEntityLoading,
  } = useQuery(
    BookingQueries.getDraftEntityQuery({
      retry: false,
      enabled: !!entityId,
      query: {
        entity,
        entityId,
      },
      staleTime: mustRefetch ? 0 : 1000 * 60 * 60,
      params: {entityId},
      refetchOnWindowFocus: false,
      refetchInterval: mustRefetch ? 1000 * 2 : false,
    }),
  )

  useEffect(() => {
    if (!mustRefetch) {
      return
    }
    if (draftEntity?.status === 'PAYMENT_SUCCESS') {
      setStartRefetch(false)

      if (entity === 'booking') {
        // The RN webview cannot reliably observe the SPA redirect below (its URL
        // listener is debounced and post-redirect route guards can re-navigate),
        // but it watches the DOM for div[title='Paid'] via a MutationObserver.
        // Drop that marker so the native payment sheet always closes on success.
        if ('ReactNativeWebView' in window) {
          const paidMarker = document.createElement('div')
          paidMarker.title = 'Paid'
          paidMarker.style.display = 'none'
          document.body.appendChild(paidMarker)
        }
        clearUrls?.()
        setIsLoading(false)
        // Caller handled navigation (e.g. opened the created-bookings modal) — don't redirect.
        if (onBookingPaymentSuccess?.()) {
          return
        }
        const redirectPath = redirectPathOverride || `/${venueId}/bookings/${draftEntity.entityId}`
        navigate(redirectPath)
      } else {
        const posParam = posTerminalProcessId ? `&posTerminalProcessId=${posTerminalProcessId}` : ''
        const redirectPath = `/clear-view/${venueId}/payment-success?entityId=${draftEntity.entityId}&entity=${entity}${posParam}`
        clearUrls?.()
        setIsLoading(false)
        navigate(redirectPath)
      }
    }
    if (draftEntity?.status === 'PAYMENT_FAIL') {
      setIsLoading(false)
      setStartRefetch(false)
      clearUrls?.()
      remove()
      updateEntityForPay({
        status: null,
        entityId: draftEntity.entityId,
        entity,
      })
      if (redirectOnFail && !posTerminalProcessId && entityId && entity) {
        navigate(`/clear-view/${venueId}/payment-fail?entityId=${entityId}&entity=${entity}`)
      } else {
        showMessage(l('paymentFailed'), 'error')
      }
    }
  }, [
    draftEntity,
    mustRefetch,
    clearUrls,
    navigate,
    venueId,
    entity,
    entityId,
    l,
    redirectPathOverride,
    setIsLoading,
    updateEntityForPay,
    remove,
    onBookingPaymentSuccess,
    redirectOnFail,
    // POS branch: the effect reads it when reporting terminal status.
    posTerminalProcessId,
  ])
  // develop widened this to (id?, onReady?); both params are optional, so the
  // POS branch's zero-arg call sites still work.
  const setDraftPendingStatus = (id?: string, onReady?: () => void) => {
    const resolvedId = id || draftEntity?.entityId
    updateEntityForPay(
      {
        entity,
        subStatus: null,
        expires_at: null,
        status: 'PAYMENT_PENDING',
        entityId: resolvedId,
      },
      {
        onSuccess: () => {
          remove()
          setStartRefetch(true)
          onReady?.()
        },
        onError: () => {
          // ignore
        },
      },
    )
  }

  return {
    error,
    draftEntity,
    setStartRefetch,
    draftEntityLoading,
    setDraftPendingStatus,
    removeDraftEntity: remove,
  }
}
